import { PoweredBy } from './PoweredBy'
import ReactMarkdown from 'react-markdown'
import type { UiMessage } from '../state/chatStore'
import { useChatStore } from '../state/chatStore'
import { FlyingFigure } from './FlyingFigure'
import { MediaCard, medienAus } from './MediaCard'
import { PendingAction } from './PendingAction'
import { useT } from '../lib/i18n'

/**
 * `ohneAktion`: Die offene Entscheidung (Kontaktkarte, Ja/Nein) nicht hier
 * zeigen — der Sprachmodus heftet sie an einer festen Stelle an, damit sie
 * bleibt, wenn die Antwort aus dem Bild wandert.
 */
export function MessageBubble({
  message,
  ohneAktion = false,
}: {
  message: UiMessage
  ohneAktion?: boolean
}) {
  const rateMessage = useChatStore((s) => s.rateMessage)
  const askMore = useChatStore((s) => s.askMore)
  const askFollowup = useChatStore((s) => s.askFollowup)
  const showcase = useChatStore((s) =>
    message.showcaseId ? s.showcases.find((sc) => sc.id === message.showcaseId) : undefined,
  )
  const showcaseTitle = showcase?.title
  const t = useT()
  const isUser = message.role === 'user'
  const canRate = !isUser && !message.streaming && message.content.length > 0
  // Der Kopf steht über einer Antwort, die einen Showcase behandelt: ein
  // Tipp zu „Mehr dazu" oder direkt zur Kontaktkarte — statt zu tippen.
  const kopf = !isUser && !message.streaming && message.showcaseId && showcaseTitle

  return (
    <div className={`bubble-in flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 ${
          isUser
            ? 'rounded-[1.375rem] rounded-br-md bg-accent text-on-accent'
            : 'rounded-[1.375rem] rounded-bl-md bg-surface text-text shadow-[var(--shadow-card)]'
        }`}
        aria-live={message.streaming ? 'polite' : undefined}
      >
        {kopf && (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-border)] pb-2">
            <span className="min-w-0 flex-1 truncate font-mono text-[0.7rem] uppercase tracking-wider text-brand">
              {showcaseTitle}
            </span>
            <button
              type="button"
              onClick={() => void askMore(message.showcaseId!)}
              className="rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold text-text hover:text-brand"
            >
              {t.more}
            </button>
            <button
              type="button"
              onClick={() => void askFollowup(message.showcaseId!)}
              className="rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold text-text hover:text-brand"
            >
              {t.handOver}
            </button>
          </div>
        )}
        {message.content ? (
          // break-words: a pasted URL or any long unbroken string would
          // otherwise run out of the bubble instead of wrapping.
          <div className="markdown-body break-words text-[0.975rem] leading-relaxed [&_a]:underline [&_a]:decoration-dotted [&_li]:ml-4 [&_li]:list-disc [&_p+p]:mt-2">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        ) : message.streaming ? (
          <FlyingFigure />
        ) : null}

        {/* Bild, Video oder PDF, das K4ia mit show_media zeigt (MediaCard). */}
        {!isUser && medienAus(message.toolCalls).map((m) => <MediaCard key={m.id} medium={m} />)}

        {/* Keine Quellenzeile unter der Antwort: FR-04 sagt „auf Nachfrage" —
            K4ia nennt Dokument und Abschnitt, wenn jemand fragt. Die Zeile
            zeigte viermal denselben Titel und stand unter Antworten, die die
            Quelle gar nicht genutzt hatten. Die Quellen bleiben in den Daten
            (Sitzung, Analytics), nur nicht mehr unter jeder Blase. */}

        {/* „powered by", wenn die Antwort einen Showcase mit Partner behandelt. */}
        {!isUser && !message.streaming && message.content && (
          <PoweredBy showcase={showcase} className="mt-3" />
        )}

        {!ohneAktion && <PendingAction message={message} />}

        {canRate && (
          <div className="mt-2 flex items-center gap-1">
            <span className="mr-1 text-xs text-text-muted">{t.helpful}</span>
            <RatingButton
              label={t.helpfulYes}
              direction="up"
              active={message.rating === 'up'}
              onClick={() => void rateMessage(message.id, 'up')}
            />
            <RatingButton
              label={t.helpfulNo}
              direction="down"
              active={message.rating === 'down'}
              onClick={() => void rateMessage(message.id, 'down')}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function RatingButton({
  label,
  direction,
  active,
  onClick,
}: {
  label: string
  direction: 'up' | 'down'
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
        active ? 'bg-surface-2 text-brand' : 'text-text-muted hover:bg-surface-2 hover:text-text'
      }`}
    >
      <ThumbIcon direction={direction} filled={active} />
    </button>
  )
}

function ThumbIcon({ direction, filled }: { direction: 'up' | 'down'; filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden="true"
      style={direction === 'down' ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path
        d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Zm0 0 4-8a3 3 0 0 1 3 3v4h5.5a2 2 0 0 1 2 2.3l-1.2 8A2 2 0 0 1 18.3 21H7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  )
}
